import { useState, useEffect, useCallback, useRef } from 'react';
import { useSSE } from '../../../contexts/SSEContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserSettings, editUser } from '../../../services/userService';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('Shell::Settings::useUserSettings');

const RESPONSE_TIMEOUT_MS = 30000;

/**
 * Module-private SSE-correlated request runner. Mirrors the same-named helper
 * in user-management/src/hooks/useUsers.js — kept inline here for one-file
 * locality rather than abstracted into a shared service module.
 */
function createSSERequest(sse, apiCall, { timeoutMs = RESPONSE_TIMEOUT_MS } = {}) {
    return new Promise((resolve, reject) => {
        const correlationId = sse.generateMessageId();
        let timeoutId = null;

        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);
        };

        const unregister = sse.registerHandler(correlationId, (data) => {
            const status = data.status;
            logger.info('SSE response received', { status, correlationId, source: data.source });

            if (status === 'success' || status === 'warning') {
                unregister();
                cleanup();
                resolve(data);
                return;
            }

            if (status === 'error') {
                unregister();
                cleanup();
                const errMsg = data.error || 'Operation failed';
                logger.error('SSE error response:', errMsg);
                reject(new Error(errMsg));
                return;
            }

            logger.warn('Unexpected status in SSE response:', status);
        });

        timeoutId = setTimeout(() => {
            unregister();
            const errMsg = 'Request timed out. Please try again.';
            logger.error(errMsg);
            reject(new Error(errMsg));
        }, timeoutMs);

        apiCall(sse.clientId, correlationId).catch((err) => {
            unregister();
            cleanup();
            logger.error('REST request failed:', err.message);
            reject(err);
        });
    });
}

/** Camel-case the snake_case fields returned by the backend. */
const toSettings = (data) => ({
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name,
    phone: data.phone,
    language: data.language,
    timezone: data.timezone,
    roles: data.roles || [],
    features: data.features || [],
    assets: data.assets || [],
    notificationMethods: data.notification_methods || {}
});

/**
 * Single entry point for the Settings page's data needs.
 *
 * Auto-fetches the calling user's settings on mount (and whenever the SSE
 * connection or auth token become available) and exposes a tiny CRUD surface
 * so each section can read fresh data and persist changes via `edit_user`.
 *
 * @returns {{
 *   settings: object | null,
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   editUser: (updates: object) => Promise<object>
 * }}
 */
export function useUserSettings() {
    const sse = useSSE();
    const { token } = useAuth();

    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Avoid stomping state from a stale fetch if the caller refetches mid-flight.
    const fetchIdRef = useRef(0);

    const refetch = useCallback(async () => {
        if (!token || !sse?.clientId) return;

        const myId = ++fetchIdRef.current;
        setLoading(true);
        setError(null);

        try {
            const data = await createSSERequest(sse, (clientId, correlationId) =>
                getUserSettings(token, clientId, correlationId)
            );
            if (myId !== fetchIdRef.current) return; // stale
            setSettings(toSettings(data));
            logger.info('Settings loaded', { email: data.email });
        } catch (err) {
            if (myId !== fetchIdRef.current) return; // stale
            setError(err.message || 'Failed to load settings');
            logger.error('Failed to load settings:', err.message);
        } finally {
            if (myId === fetchIdRef.current) setLoading(false);
        }
    }, [sse, token]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    /**
     * Sends a partial update to edit_user, then refetches so `settings`
     * reflects the persisted state. Caller must include `email` in updates;
     * everything else (phone, language, ...) is optional.
     */
    const editUserAction = useCallback(async (updates) => {
        if (!updates?.email) {
            throw new Error('email is required to identify the user');
        }
        if (!token || !sse?.clientId) {
            throw new Error('Auth/SSE not ready');
        }

        const data = await createSSERequest(sse, (clientId, correlationId) =>
            editUser(token, clientId, correlationId, updates)
        );
        await refetch();
        return data;
    }, [sse, token, refetch]);

    return {
        settings,
        loading,
        error,
        refetch,
        editUser: editUserAction
    };
}